import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNotesPanel } from './useNotesPanel';
import NotesIcon from './NotesIcon';
import NoteCard from './NoteCard';
import { CalendarIcon } from './NoteTagIcons';
import './notes.css';

function Composer({ panel }) {
  return (
    <div className="rn-composer">
      <textarea
        placeholder="Add a note…"
        value={panel.draftText}
        onChange={(e) => panel.setDraftText(e.target.value)}
        autoFocus
      />
      <div className="rn-composer-row">
        <label className="rn-composer-left" style={{ fontSize: 11, color: '#6B7280', alignItems: 'center' }}>
          <CalendarIcon /> Reminder
          <input
            type="date"
            value={panel.draftReminder}
            onChange={(e) => panel.setDraftReminder(e.target.value)}
            style={{ fontSize: 11, border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 6px', marginLeft: 6 }}
          />
        </label>
        <button type="button" className="rn-cancel-btn" onClick={() => panel.setComposerOpen(false)}>Cancel</button>
        <button type="button" className="rn-save-btn" disabled={!panel.draftText.trim() || panel.saving} onClick={panel.saveComposer}>
          {panel.saving ? 'Saving…' : 'Save Note'}
        </button>
      </div>
    </div>
  );
}

function NoteStack({ panel }) {
  return (
    <div className="rn-stack">
      {panel.notes.map((n) => (
        <NoteCard key={n.id} note={n} variant="inline" onUpdate={panel.handleUpdate} onDelete={panel.handleDelete} />
      ))}
      {panel.composerOpen ? (
        <Composer panel={panel} />
      ) : (
        <button type="button" className="rn-add-another" onClick={panel.openComposer}>+ Add note</button>
      )}
    </div>
  );
}

/**
 * Pen-icon + count-badge button only — internal to this file, used by the
 * default export below (both its normal and `portalTargetId` presentations
 * render the same trigger inline).
 */
function NotesTrigger({ panel }) {
  return (
    <button
      type="button"
      className={`rn-icon-btn${panel.count > 0 ? ' rn-has-notes' : ''}`}
      title={panel.count > 0 ? `${panel.count} note${panel.count === 1 ? '' : 's'}` : 'Add a note'}
      onClick={() => (panel.count > 0 ? panel.setExpanded((e) => !e) : panel.openComposer())}
    >
      <NotesIcon size={14} />
      {panel.count > 0 && <span className="rn-count-badge">{panel.count}</span>}
    </button>
  );
}

/**
 * Notes affordance dropped into Property detail, Documents, Parties,
 * Conversations, and Timeline. Self-contained: fetches its own notes by
 * (linkType, linkId),
 * renders the notes icon + count badge, and one of two presentations for
 * the list itself. Every note created here is the exact same record shown
 * in My Notes — not a copy — since both read/write the same
 * /api/v1/users/me/notes API.
 *
 * `linkLabel` is required to CREATE a note here (denormalized display-label
 * snapshot, e.g. "214 Maple Ct — Inspection Contingency") — the backend has
 * no synchronous access to resolve one itself.
 *
 * `transactionId`/`transactionName`: pass these when this panel lives inside a
 * transaction's Timeline/Parties/Documents tab (where `linkId` above points at
 * a milestone/participant/document, not the transaction itself) so My Notes
 * can later show which transaction the note belongs to and link back to it.
 * Omit for Property/Conversation, which aren't transaction-scoped.
 *
 * `popover`: for a host row with a fixed/constrained height (e.g. a Parties
 * or Conversations list row), where an inline-expanding stack would break
 * the layout — renders the list as a `position: fixed` floating panel
 * instead, anchored to the trigger's own on-screen rect (recomputed on open,
 * closed on scroll/resize/outside-click/Escape) so it floats above the row
 * instead of being clipped by or overlapping into neighbors inside the
 * host list's own scroll container — same technique this codebase uses for
 * TransactionDocuments' kebab menu.
 *
 * `portalTargetId`: for a host row where the trigger must stay inline (e.g.
 * next to other action buttons) but the expanded toggle+stack needs to
 * render as its own full-width block elsewhere in the DOM (matching the
 * reference wireframe's Timeline layout, where the notes block sits below
 * the whole action row, not squeezed inside it) — portals the
 * toggle+stack into the element with this `id` instead of rendering it
 * inline. Falls back to normal inline rendering if that element isn't
 * found (e.g. not mounted yet), so this never silently loses the UI.
 */
export default function NotesPanel({ linkType, linkId, linkLabel, transactionId, transactionName, className, popover = false, portalTargetId }) {
  const panel = useNotesPanel({ linkType, linkId, linkLabel, transactionId, transactionName });
  const [portalNode, setPortalNode] = useState(null);
  const triggerWrapRef = useRef(null);
  const popoverRef = useRef(null);
  // popover mode renders `position: fixed`, coordinates computed from the
  // trigger's own on-screen rect (same technique as TransactionDocuments'
  // kebab menu — see docMenuPos there) instead of `position: absolute`
  // anchored to the row. `absolute` positioned it relative to the row inside
  // whatever scroll container hosts the list (e.g. Parties, Conversations),
  // so it could overlap/clip against neighboring rows instead of floating
  // cleanly above them.
  const [popoverPos, setPopoverPos] = useState(null);

  useEffect(() => {
    if (!portalTargetId) { setPortalNode(null); return; }
    setPortalNode(document.getElementById(portalTargetId));
  }, [portalTargetId, panel.expanded, panel.count]);

  useEffect(() => {
    if (!popover || !panel.expanded) { setPopoverPos(null); return undefined; }
    const FLYOUT_WIDTH = 320;
    const updatePos = () => {
      const rect = triggerWrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      let left = rect.right - FLYOUT_WIDTH;
      const maxLeft = window.innerWidth - FLYOUT_WIDTH - 8;
      if (left > maxLeft) left = maxLeft;
      if (left < 8) left = 8;
      setPopoverPos({ top: rect.bottom + 6, left });
    };
    updatePos();
    // A fixed-position flyout would visually detach from its anchor once the
    // host list scrolls (or the window resizes) — close it instead, same
    // rationale as the kebab menu's own handleScroll.
    const close = () => panel.setExpanded(false);
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)
          && triggerWrapRef.current && !triggerWrapRef.current.contains(e.target)) {
        panel.setExpanded(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') panel.setExpanded(false); };
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popover, panel.expanded]);

  const expandContent = (
    <>
      {panel.count > 0 && (
        <button
          type="button"
          className={`rn-toggle${panel.expanded ? ' rn-expanded' : ''}`}
          onClick={() => panel.setExpanded((e) => !e)}
        >
          <span className="rn-chevron">▶</span>
          <span>{panel.count} note{panel.count === 1 ? '' : 's'}</span>
        </button>
      )}
      {panel.expanded && <NoteStack panel={panel} />}
    </>
  );

  if (portalTargetId) {
    return (
      <div className={className}>
        <NotesTrigger panel={panel} />
        {portalNode ? createPortal(expandContent, portalNode) : expandContent}
      </div>
    );
  }

  return (
    <div
      className={className}
      ref={popover ? triggerWrapRef : null}
      style={popover ? { position: 'relative', display: 'inline-block' } : undefined}
    >
      <NotesTrigger panel={panel} />

      {!popover && expandContent}

      {popover && panel.expanded && popoverPos && (
        <div
          ref={popoverRef}
          style={{
            position: 'fixed', top: popoverPos.top, left: popoverPos.left, width: 320,
            maxHeight: 320, overflowY: 'auto', background: '#fff', border: '1px solid #E5E7EB',
            borderRadius: 10, boxShadow: '0 4px 16px rgba(20,10,50,.12)', padding: 12, zIndex: 200,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <NoteStack panel={panel} />
        </div>
      )}
    </div>
  );
}
